/**
 * useNavigationLogging - Automatically track screen views
 * 
 * Add this hook to any screen to automatically log screen views when the screen comes into focus
 * 
 * Usage:
 *   useNavigationLogging('HomePage');
 *   // or auto-detect from route:
 *   useNavigationLogging();
 */

import { useEffect, useRef } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { loggingService } from '../services/loggingService';

export const useNavigationLogging = (screenName?: string) => {
  const navigation = useNavigation();
  const route = useRoute();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    const name = screenName || route.name;
    
    // Log screen view on mount and when screen comes into focus
    const unsubscribe = navigation.addListener('focus', () => {
      if (!hasLoggedRef.current) {
        // Only pass lightweight IDs to determine log value
        const routeParams: any = route.params || {};
        const valueParams: Record<string, any> = {};
        if (routeParams.bookId) valueParams.bookId = routeParams.bookId;
        if (routeParams.insightId) valueParams.insightId = routeParams.insightId;
        if (routeParams.chapterId) valueParams.chapterId = routeParams.chapterId;
        if (routeParams.itemId) valueParams.itemId = routeParams.itemId;
        loggingService.logScreenView(name, valueParams);
        hasLoggedRef.current = true;
      }
    });

    // Reset flag when screen loses focus
    const blurUnsubscribe = navigation.addListener('blur', () => {
      hasLoggedRef.current = false;
    });

    return () => {
      unsubscribe();
      blurUnsubscribe();
    };
  }, [navigation, route, screenName]);
};

